import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  Chip,
  Button,
  Grid,
  IconButton,
  Dialog,
  DialogContent,
  Avatar,
} from "@mui/material";
import {
  Create as CreateIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  Grade as GradeIcon,
  Comment as CommentIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material";
import HtmlRenderer from "./HtmlRenderer";

/* ---------------------------------------------------------------
   Brand Colors (Only white and red)
--------------------------------------------------------------- */
const BRAND_PRIMARY = "#9D2235";
const BRAND_DARK = "#7a1a2e";
const BRAND_LIGHT = "#c73e54";
const BRAND_BG = "#fdf2f4";
const SUCCESS_GREEN = "#4caf50";
const SUCCESS_BG = "#e8f5e9";

/* ---------------------------------------------------------------
   OET Grade Helper
--------------------------------------------------------------- */
function getOetGrade(marks) {
  if (marks == null) return null;
  const score = Number(marks);
  if (score >= 450) return { grade: "A", color: SUCCESS_GREEN };
  if (score >= 400) return { grade: "B", color: "#2196f3" };
  if (score >= 350) return { grade: "C+", color: "#ff9800" };
  if (score >= 300) return { grade: "C", color: "#ff9800" };
  if (score >= 200) return { grade: "D", color: BRAND_PRIMARY };
  return { grade: "E", color: BRAND_DARK };
}

/* ---------------------------------------------------------------
   Image Viewer Modal
--------------------------------------------------------------- */
function ImageViewerModal({ open, onClose, imageUrl, imageName }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "90vh",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {imageName || "Submitted Image"}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent sx={{ p: 0, display: "flex", justifyContent: "center", bgcolor: "#f5f5f5" }}>
        <img
          src={imageUrl}
          alt={imageName || "Writing submission"}
          style={{
            maxWidth: "100%",
            maxHeight: "75vh",
            objectFit: "contain",
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

/* ---------------------------------------------------------------
   Case Notes Section (Left Panel) - Matches WritingSection.js exactly
--------------------------------------------------------------- */
function CaseNotesSection({ caseNotes = [] }) {
  if (!caseNotes.length) {
    return (
      <Box sx={{ textAlign: "center", py: 4, color: "#64748b" }}>
        <DescriptionIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 2 }} />
        <Typography variant="body2">No case notes available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="h6"
        sx={{
          fontWeight: "bold",
          mb: { xs: 1, sm: 2 },
          fontSize: { xs: "1rem", sm: "1.25rem" },
        }}
      >
        Case Notes
      </Typography>

      {caseNotes.map((note, idx) => (
        <Card
          key={note._id || idx}
          elevation={2}
          sx={{
            mb: { xs: 2, sm: 3 },
            p: { xs: 1.5, sm: 3 },
            borderRadius: 2,
            boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
            background: "#fff",
          }}
        >
          {note.title && (
            <HtmlRenderer
              content={note.title}
              component="h6"
              sx={{
                fontWeight: 700,
                mb: { xs: 1.5, sm: 2 },
                color: "#9D2235",
                fontSize: { xs: "1rem", sm: "1.25rem" },
              }}
            />
          )}

          {(!note.subSections || note.subSections.length === 0) &&
            note.content && (
              <HtmlRenderer
                content={note.content}
                component="div"
                sx={{
                  mb: { xs: 1.5, sm: 2 },
                  lineHeight: { xs: 1.6, sm: 1.8 },
                  whiteSpace: "pre-line",
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                }}
              />
            )}

          {note.subSections?.length > 0 && (
            <Box sx={{ ml: 2, mb: 1 }}>
              {note.subSections.length > 1 ? (
                <Box component="ul" sx={{ listStyle: "disc", pl: 5 }}>
                  {note.subSections.map((sub, sidx) => (
                    <Box component="li" key={sub._id || sidx} sx={{ mb: 1 }}>
                      {sub.label && (
                        <HtmlRenderer
                          content={`${sub.label}${sub.label ? ": " : ""}`}
                          component="span"
                          sx={{
                            fontWeight: 500,
                            fontSize: { xs: "0.875rem", sm: "1rem" },
                          }}
                        />
                      )}
                      {sub.content && (
                        <HtmlRenderer
                          content={sub.content}
                          component="span"
                          sx={{
                            fontSize: { xs: "0.875rem", sm: "1rem" },
                          }}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box>
                  {note.subSections[0].label && (
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 500,
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                      }}
                    >
                      {note.subSections[0].label}
                      {note.subSections[0].label ? ": " : ""}
                    </Typography>
                  )}
                  {note.subSections[0].content && (
                    <HtmlRenderer
                      content={note.subSections[0].content}
                      component="div"
                      sx={{
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                      }}
                    />
                  )}
                </Box>
              )}
            </Box>
          )}
        </Card>
      ))}
    </Box>
  );
}

/* ---------------------------------------------------------------
   Writing Task & Submission Section (Right Panel)
--------------------------------------------------------------- */
function WritingTaskSection({
  writingPrompt,
  instructions,
  submittedText,
  submittedImages,
  isPaperBased,
  evaluationPdf,
  marks,
  grade,
  adminComments,
  createdAt,
  isEvaluated,
  checkingStatus,
}) {
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const gradeInfo = marks !== null && marks !== undefined ? getOetGrade(marks) : null;

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setImageModalOpen(true);
  };

  const wordCount = submittedText
    ? submittedText.trim().split(/\s+/).filter(Boolean).length
    : 0;

  return (
    <Box sx={{ py: { xs: 1, sm: 2 } }}>
      {/* Writing Task Card */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          border: "1px solid #e2e8f0",
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            p: 2,
            bgcolor: "#f8fafc",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <AssignmentIcon sx={{ color: BRAND_PRIMARY }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#1e293b",
                fontSize: { xs: "1rem", sm: "1.25rem" },
              }}
            >
              Writing Task
            </Typography>
          </Box>
        </Box>

        <CardContent>
          {/* Instructions */}
          <Typography
            sx={{
              fontWeight: 600,
              mb: 1.5,
              color: BRAND_PRIMARY,
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}
          >
            Instructions
          </Typography>
          <Typography
            sx={{
              mb: 2,
              color: "#374151",
              fontSize: { xs: "0.875rem", sm: "1rem" },
              lineHeight: 1.7,
            }}
          >
            Read the case notes and complete the writing task which follows.
          </Typography>

          {/* Writing Prompt */}
          {writingPrompt && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: "#fff",
                borderRadius: 2,
              }}
            >
              <HtmlRenderer
                content={writingPrompt}
                component="div"
                sx={{
                  lineHeight: 1.7,
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  color: "#374151",
                }}
              />
            </Paper>
          )}
        </CardContent>
      </Card>

      {/* Student Submission Card */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          border: "1px solid #e2e8f0",
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            p: 2,
            bgcolor: BRAND_BG,
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {submittedImages && submittedImages.length > 0 ? (
              <ImageIcon sx={{ color: BRAND_PRIMARY }} />
            ) : (
              <CreateIcon sx={{ color: BRAND_PRIMARY }} />
            )}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#1e293b",
                fontSize: { xs: "1rem", sm: "1.25rem" },
              }}
            >
              {submittedImages && submittedImages.length > 0 ? "Paper-Based Submission" : "Student Submission"}
            </Typography>
          </Box>

          {createdAt && (
            <Chip
              icon={<ScheduleIcon sx={{ fontSize: 16 }} />}
              label={new Date(createdAt).toLocaleString()}
              size="small"
              variant="outlined"
              sx={{ borderColor: BRAND_PRIMARY, color: BRAND_PRIMARY }}
            />
          )}
        </Box>

        <CardContent>
          {/* Show Images if they exist (Paper-Based submission) */}
          {submittedImages && submittedImages.length > 0 ? (
            <Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: "#64748b",
                  fontSize: "0.875rem",
                }}
              >
                Uploaded Images ({submittedImages.length})
              </Typography>
              <Grid container spacing={2}>
                {submittedImages.map((image, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        overflow: "hidden",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        "&:hover": {
                          transform: "scale(1.02)",
                          boxShadow: 3,
                        },
                      }}
                      onClick={() => handleImageClick(image)}
                    >
                      <Box sx={{ position: "relative" }}>
                        <img
                          src={image.imageUrl}
                          alt={`Writing submission ${index + 1}`}
                          style={{
                            width: "100%",
                            height: 200,
                            objectFit: "cover",
                          }}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            bgcolor: "rgba(0,0,0,0.6)",
                            borderRadius: 1,
                            p: 0.5,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <ZoomInIcon sx={{ color: "#fff", fontSize: 16 }} />
                          <Typography sx={{ color: "#fff", fontSize: 11 }}>
                            Click to view
                          </Typography>
                        </Box>
                      </Box>
                      {image.originalName && (
                        <Box sx={{ p: 1.5, bgcolor: "#f8fafc" }}>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {image.originalName}
                          </Typography>
                        </Box>
                      )}
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : submittedText ? (
            /* Computer-Based: Show Text (also fallback if no images but has text) */
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "#64748b",
                    fontSize: "0.875rem",
                  }}
                >
                  Written Response
                </Typography>
                <Chip
                  label={`${wordCount} words`}
                  size="small"
                  sx={{
                    bgcolor: wordCount >= 180 && wordCount <= 200 ? SUCCESS_BG : BRAND_BG,
                    color: wordCount >= 180 && wordCount <= 200 ? SUCCESS_GREEN : BRAND_PRIMARY,
                    fontWeight: 600,
                  }}
                />
              </Box>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  bgcolor: "#fff",
                  borderRadius: 2,
                  minHeight: 200,
                }}
              >
                <Typography
                  sx={{
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.8,
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    color: "#374151",
                    fontFamily: "'Georgia', serif",
                  }}
                >
                  {submittedText}
                </Typography>
              </Paper>
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 4, color: "#64748b" }}>
              <DescriptionIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 2 }} />
              <Typography variant="body2">No submission found</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Evaluation Card */}
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${isEvaluated ? SUCCESS_GREEN : "#e2e8f0"}`,
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            p: 2,
            bgcolor: isEvaluated ? SUCCESS_BG : "#f8fafc",
            borderBottom: `1px solid ${isEvaluated ? SUCCESS_GREEN : "#e2e8f0"}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {isEvaluated ? (
              <CheckCircleIcon sx={{ color: SUCCESS_GREEN }} />
            ) : (
              <ScheduleIcon sx={{ color: "#64748b" }} />
            )}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: isEvaluated ? SUCCESS_GREEN : "#64748b",
                fontSize: { xs: "1rem", sm: "1.25rem" },
              }}
            >
              {isEvaluated ? "Assessor Evaluation" : "Pending Evaluation"}
            </Typography>
          </Box>

          {isEvaluated && marks !== null && marks !== undefined && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ textAlign: "right" }}>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: gradeInfo?.color || BRAND_PRIMARY }}
                >
                  {marks}
                </Typography>
              </Box>
              <Chip
                icon={<GradeIcon sx={{ fontSize: 16 }} />}
                label={`Grade ${grade || gradeInfo?.grade}`}
                sx={{
                  bgcolor: `${gradeInfo?.color || BRAND_PRIMARY}20`,
                  color: gradeInfo?.color || BRAND_PRIMARY,
                  fontWeight: 600,
                }}
              />
            </Box>
          )}
        </Box>

        <CardContent>
          {isEvaluated ? (
            <Box>
              {/* Assessor Comments */}
              {adminComments && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                    <CommentIcon sx={{ color: BRAND_PRIMARY, fontSize: 20 }} />
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: "#1e293b",
                        fontSize: "0.95rem",
                      }}
                    >
                      Examiner's Comments
                    </Typography>
                  </Box>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      bgcolor: "#fff",
                      borderRadius: 2,
                      borderLeft: `3px solid ${BRAND_PRIMARY}`,
                    }}
                  >
                    <Typography
                      sx={{
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.7,
                        color: "#374151",
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                      }}
                    >
                      {adminComments}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* Evaluation PDF Download */}
              {evaluationPdf && (
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                    <PdfIcon sx={{ color: BRAND_PRIMARY, fontSize: 20 }} />
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: "#1e293b",
                        fontSize: "0.95rem",
                      }}
                    >
                      Detailed Evaluation Report
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                    href={evaluationPdf}
                    target="_blank"
                    sx={{
                      bgcolor: BRAND_PRIMARY,
                      "&:hover": { bgcolor: BRAND_DARK },
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      px: 3,
                    }}
                  >
                    Download Evaluation PDF
                  </Button>
                </Box>
              )}

              {!adminComments && !evaluationPdf && (
                <Typography sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                  Your writing has been evaluated. Score and grade are shown above.
                </Typography>
              )}
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  bgcolor: BRAND_BG,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <ScheduleIcon sx={{ fontSize: 32, color: BRAND_LIGHT }} />
              </Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: BRAND_PRIMARY, mb: 1 }}
              >
                Evaluation in Progress
              </Typography>
              <Typography sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                Your writing will be evaluated within 24 hours. Please check back later
                for your results.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Image Viewer Modal */}
      <ImageViewerModal
        open={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        imageUrl={selectedImage?.imageUrl}
        imageName={selectedImage?.originalName}
      />
    </Box>
  );
}

/* ---------------------------------------------------------------
   Writing Evaluation Section (for multi-task view)
--------------------------------------------------------------- */
function WritingEvaluationSection({
  evaluationPdf,
  marks,
  grade,
  adminComments,
  isEvaluated,
  checkingStatus,
}) {
  const gradeInfo = marks !== null && marks !== undefined ? getOetGrade(marks) : null;

  return (
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${isEvaluated ? SUCCESS_GREEN : "#e2e8f0"}`,
        borderRadius: 2,
        mt: 2,
      }}
    >
      <Box
        sx={{
          p: 2,
          bgcolor: isEvaluated ? SUCCESS_BG : "#f8fafc",
          borderBottom: `1px solid ${isEvaluated ? SUCCESS_GREEN : "#e2e8f0"}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {isEvaluated ? (
            <CheckCircleIcon sx={{ color: SUCCESS_GREEN }} />
          ) : (
            <ScheduleIcon sx={{ color: "#64748b" }} />
          )}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: isEvaluated ? SUCCESS_GREEN : "#64748b",
              fontSize: { xs: "1rem", sm: "1.25rem" },
            }}
          >
            {isEvaluated ? "Overall Evaluation" : "Pending Evaluation"}
          </Typography>
        </Box>

        {isEvaluated && marks !== null && marks !== undefined && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: gradeInfo?.color || BRAND_PRIMARY }}
            >
              {marks}
            </Typography>
            {grade && (
              <Chip
                icon={<GradeIcon sx={{ fontSize: 16 }} />}
                label={`Grade ${grade || gradeInfo?.grade}`}
                sx={{
                  bgcolor: `${gradeInfo?.color || BRAND_PRIMARY}20`,
                  color: gradeInfo?.color || BRAND_PRIMARY,
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
        )}
      </Box>

      <CardContent>
        {isEvaluated ? (
          <Box>
            {adminComments && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <CommentIcon sx={{ color: BRAND_PRIMARY, fontSize: 18 }} />
                  <Typography sx={{ fontWeight: 600, color: "#1e293b", fontSize: "0.9rem" }}>
                    Examiner's Comments
                  </Typography>
                </Box>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: "#fff",
                    borderRadius: 2,
                    borderLeft: `3px solid ${BRAND_PRIMARY}`,
                  }}
                >
                  <Typography
                    sx={{
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.6,
                      color: "#374151",
                      fontSize: "0.9rem",
                    }}
                  >
                    {adminComments}
                  </Typography>
                </Paper>
              </Box>
            )}

            {evaluationPdf && (
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                href={evaluationPdf}
                target="_blank"
                sx={{
                  bgcolor: BRAND_PRIMARY,
                  "&:hover": { bgcolor: BRAND_DARK },
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Download Evaluation PDF
              </Button>
            )}
          </Box>
        ) : (
          <Box sx={{ textAlign: "center", py: 2 }}>
            <ScheduleIcon sx={{ fontSize: 40, color: BRAND_LIGHT, mb: 1 }} />
            <Typography sx={{ fontWeight: 600, color: BRAND_PRIMARY, mb: 0.5 }}>
              Evaluation in Progress
            </Typography>
            <Typography sx={{ color: "#64748b", fontSize: "0.85rem" }}>
              Your writing will be evaluated within 24 hours.
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

/* ---------------------------------------------------------------
   Main WritingSectionReview Component
--------------------------------------------------------------- */
export default function WritingSectionReview({
  writingData = {},
  submissionData = {},
}) {
  // Extract data from props - API returns 'sections' which we display as case notes
  // writingData.test.sections OR writingData.sections OR writingData.caseNotes
  const caseNotes = writingData.sections || writingData.caseNotes || writingData.test?.sections || [];
  const writingPrompt = writingData.writingPrompt || writingData.test?.writingPrompt || "";
  const instructions = writingData.instructions || writingData.test?.instructions || "";
  const testTitle = writingData.title || writingData.test?.title || "";
  
  // Get writing tasks from both writingData and test
  const writingTasks = writingData.writingTasks || writingData.test?.tasks || [];
  const submissionTasks = submissionData.writingTasks || [];

  // Submission data (aggregated for backward compatibility)
  const submittedText = submissionData.writingText || "";
  const submittedImages = submissionData.writingImage || [];
  const isPaperBased = submissionData.isPictureBased || submittedImages.length > 0;
  const evaluationPdf = submissionData.evaluationPdf || submissionData.accessorFeedBackInPdf?.pdfUrl || "";
  const marks = submissionData.marks ?? submissionData.score;
  const grade = submissionData.grade || "";
  const adminComments = submissionData.adminComments || submissionData.accessorFeedBack || "";
  const createdAt = submissionData.createdAt || "";
  const isEvaluated = (marks !== null && marks !== undefined) || !!evaluationPdf;
  const checkingStatus = submissionData.checkingStatus || "";
  const evaluatorName = submissionData.evaluatorId?.fullName || "";

  // Determine if we should show multi-task view (IELTS with multiple tasks)
  const hasMultipleTasks = submissionTasks.length > 1 || writingTasks.length > 1;

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        bgcolor: "#fff",
      }}
    >
      {/* Header */}
      <Card
        elevation={0}
        sx={{
          mb: 0,
          p: { xs: 1.5, sm: 2 },
          bgcolor: "#fff",
          borderBottom: "1px solid #e2e8f0",
          borderRadius: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {/* Left - Title */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                bgcolor: BRAND_PRIMARY,
                width: 40,
                height: 40,
              }}
            >
              <CreateIcon sx={{ fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: BRAND_PRIMARY,
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                }}
              >
                Writing Module Review
              </Typography>
              <Typography
                sx={{ color: "#64748b", fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
              >
                {testTitle || (isPaperBased ? "Paper-Based Test" : "Computer-Based Test")}
              </Typography>
            </Box>
          </Box>

          {/* Right - Status */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Chip
              icon={
                isEvaluated ? (
                  <CheckCircleIcon sx={{ fontSize: 16 }} />
                ) : (
                  <ScheduleIcon sx={{ fontSize: 16 }} />
                )
              }
              label={checkingStatus === "pending" ? "Pending Evaluation" : isEvaluated ? "Evaluated" : "Pending"}
              sx={{
                bgcolor: isEvaluated ? SUCCESS_BG : BRAND_BG,
                color: isEvaluated ? SUCCESS_GREEN : BRAND_PRIMARY,
                fontWeight: 600,
              }}
            />
            {isEvaluated && marks !== null && marks !== undefined && (
              <Chip
                label={`Score: ${marks}`}
                sx={{
                  bgcolor: `${getOetGrade(marks)?.color || BRAND_PRIMARY}20`,
                  color: getOetGrade(marks)?.color || BRAND_PRIMARY,
                  fontWeight: 700,
                }}
              />
            )}
            {evaluatorName && (
              <Chip
                label={`Evaluator: ${evaluatorName}`}
                size="small"
                variant="outlined"
                sx={{ borderColor: "#e2e8f0" }}
              />
            )}
          </Box>
        </Box>
      </Card>

      {/* Split View - Case Notes & Writing Task */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          height: { xs: "auto", md: "calc(100vh - 80px)" },
          overflow: "hidden",
        }}
      >
        {/* Left Panel - Writing Tasks/Instructions from Test */}
        <Box
          sx={{
            width: { xs: "100%", md: "50%" },
            height: "100%",
            overflowY: "auto",
            overflowX: "hidden",
            p: { xs: 1, sm: 2 },
            borderRight: { md: "2px solid #e0e0e0" },
            borderBottom: { xs: "2px solid #e0e0e0", md: "none" },
            background: "#fff",
          }}
          className="custom-scrollbar"
        >
          {/* Show writing tasks if available, otherwise show case notes */}
          {writingTasks.length > 0 ? (
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  mb: { xs: 1, sm: 2 },
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                  color: "#1e293b",
                }}
              >
                Writing Tasks
              </Typography>
              {writingTasks.map((task, idx) => (
                <Card
                  key={task._id || idx}
                  elevation={1}
                  sx={{
                    mb: { xs: 2, sm: 3 },
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: 2,
                    boxShadow: "0px 2px 8px rgba(0,0,0,0.06)",
                    background: "#fff",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      mb: { xs: 1, sm: 1.5 },
                      color: BRAND_PRIMARY,
                      fontSize: { xs: "1.05rem", sm: "1.15rem" },
                    }}
                  >
                    {task.taskTitle || `Task ${task.taskNumber || idx + 1}`}
                  </Typography>
                  {task.instructions && (
                    <Box
                      sx={{
                        p: 1.5,
                        mb: 2,
                        bgcolor: "#f0f9ff",
                        borderRadius: 1,
                        borderLeft: "3px solid #3b82f6",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#1e40af",
                          fontSize: { xs: "0.85rem", sm: "0.9rem" },
                          fontWeight: 500,
                        }}
                      >
                        {task.instructions}
                      </Typography>
                    </Box>
                  )}
                  {task.task && (
                    <Box>
                      <Typography
                        sx={{
                          color: "#1e293b",
                          fontSize: { xs: "0.875rem", sm: "0.95rem" },
                          lineHeight: 1.7,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {task.task}
                      </Typography>
                    </Box>
                  )}
                </Card>
              ))}
            </Box>
          ) : (
            <CaseNotesSection caseNotes={caseNotes} />
          )}
        </Box>

        {/* Right Panel - Writing Task Submissions */}
        <Box
          sx={{
            width: { xs: "100%", md: "50%" },
            height: "100%",
            overflowY: "auto",
            overflowX: "hidden",
            p: { xs: 1, sm: 2 },
            background: "#f8f9fa",
          }}
          className="custom-scrollbar"
        >
          {/* Show individual task submissions if available */}
          {hasMultipleTasks && submissionTasks.length > 0 ? (
            <Box sx={{ py: { xs: 1, sm: 2 } }}>
              {submissionTasks.map((task, idx) => {
                const taskImages = task.writingImage || [];
                const taskText = task.writingText || "";
                const taskCheckingStatus = task.checkingStatus || "";
                const taskScore = task.score;
                const taskGrade = task.grade;
                const taskEvaluated = (taskScore !== null && taskScore !== undefined) || task.accessorFeedBackInPdf?.pdfUrl;
                const taskEvaluatorName = task.evaluatorId?.fullName || evaluatorName;
                
                return (
                  <Card
                    key={task._id || idx}
                    elevation={0}
                    sx={{
                      mb: 3,
                      border: "1px solid #e2e8f0",
                      borderRadius: 2,
                    }}
                  >
                    {/* Task Header */}
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: BRAND_BG,
                        borderBottom: "1px solid #e2e8f0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        {taskImages.length > 0 ? (
                          <ImageIcon sx={{ color: BRAND_PRIMARY }} />
                        ) : (
                          <CreateIcon sx={{ color: BRAND_PRIMARY }} />
                        )}
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: "#1e293b",
                            fontSize: { xs: "1rem", sm: "1.1rem" },
                          }}
                        >
                          Task {task.taskNumber || idx + 1} - {task.taskType === "letter" ? "Letter" : task.taskType === "essay" ? "Essay" : "Response"}
                        </Typography>
                      </Box>
                      <Chip
                        icon={
                          taskEvaluated ? (
                            <CheckCircleIcon sx={{ fontSize: 14 }} />
                          ) : (
                            <ScheduleIcon sx={{ fontSize: 14 }} />
                          )
                        }
                        label={taskCheckingStatus === "pending" ? "Pending" : taskEvaluated ? "Evaluated" : "Pending"}
                        size="small"
                        sx={{
                          bgcolor: taskEvaluated ? SUCCESS_BG : BRAND_BG,
                          color: taskEvaluated ? SUCCESS_GREEN : BRAND_PRIMARY,
                          fontWeight: 600,
                          fontSize: "0.75rem",
                        }}
                      />
                    </Box>

                    {/* Task Submission Content */}
                    <CardContent>
                      {taskImages.length > 0 ? (
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              mb: 2,
                              color: "#64748b",
                              fontSize: "0.875rem",
                            }}
                          >
                            Uploaded Images ({taskImages.length})
                          </Typography>
                          <Grid container spacing={2}>
                            {taskImages.map((image, imgIdx) => (
                              <Grid item xs={12} sm={6} key={imgIdx}>
                                <Card
                                  variant="outlined"
                                  sx={{
                                    borderRadius: 2,
                                    overflow: "hidden",
                                    cursor: "pointer",
                                    transition: "all 0.2s",
                                    "&:hover": {
                                      transform: "scale(1.02)",
                                      boxShadow: 3,
                                    },
                                  }}
                                  onClick={() => window.open(image.imageUrl, "_blank")}
                                >
                                  <Box sx={{ position: "relative" }}>
                                    <img
                                      src={image.imageUrl}
                                      alt={`Task ${task.taskNumber} submission ${imgIdx + 1}`}
                                      style={{
                                        width: "100%",
                                        height: 180,
                                        objectFit: "cover",
                                      }}
                                    />
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        top: 8,
                                        right: 8,
                                        bgcolor: "rgba(0,0,0,0.6)",
                                        borderRadius: 1,
                                        p: 0.5,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      <ZoomInIcon sx={{ color: "#fff", fontSize: 14 }} />
                                      <Typography sx={{ color: "#fff", fontSize: 10 }}>
                                        View
                                      </Typography>
                                    </Box>
                                  </Box>
                                  {image.originalName && (
                                    <Box sx={{ p: 1, bgcolor: "#f8fafc" }}>
                                      <Typography variant="caption" color="text.secondary" noWrap>
                                        {image.originalName}
                                      </Typography>
                                    </Box>
                                  )}
                                </Card>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      ) : taskText ? (
                        <Box>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 2,
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: "#64748b",
                                fontSize: "0.875rem",
                              }}
                            >
                              Written Response
                            </Typography>
                            <Chip
                              label={`${taskText.trim().split(/\s+/).filter(Boolean).length} words`}
                              size="small"
                              sx={{
                                bgcolor: BRAND_BG,
                                color: BRAND_PRIMARY,
                                fontWeight: 600,
                              }}
                            />
                          </Box>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              bgcolor: "#fff",
                              borderRadius: 2,
                              minHeight: 100,
                            }}
                          >
                            <Typography
                              sx={{
                                whiteSpace: "pre-wrap",
                                lineHeight: 1.7,
                                fontSize: { xs: "0.875rem", sm: "0.95rem" },
                                color: "#374151",
                              }}
                            >
                              {taskText}
                            </Typography>
                          </Paper>
                        </Box>
                      ) : (
                        <Box sx={{ textAlign: "center", py: 3, color: "#64748b" }}>
                          <DescriptionIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
                          <Typography variant="body2">No submission for this task</Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
              
              {/* Overall Evaluation Card */}
              <WritingEvaluationSection
                evaluationPdf={evaluationPdf}
                marks={marks}
                grade={grade}
                adminComments={adminComments}
                isEvaluated={isEvaluated}
                checkingStatus={checkingStatus}
              />
            </Box>
          ) : (
            <WritingTaskSection
              writingPrompt={writingPrompt}
              instructions={instructions}
              submittedText={submittedText}
              submittedImages={submittedImages}
              isPaperBased={isPaperBased}
              evaluationPdf={evaluationPdf}
              marks={marks}
              grade={grade}
              adminComments={adminComments}
              createdAt={createdAt}
              isEvaluated={isEvaluated}
              checkingStatus={checkingStatus}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
